"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, Building2, CircleCheck as CheckCircle2, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const commercialFeatures = [{
  id: 1,
  title: "Accredited safety compliance",
  description: "Full TG20:21 design conformance and active NASC and CHAS certified site practices."
}, {
  id: 2,
  title: "Dedicated site supervision",
  description: "Experienced supervisors coordinate inspections and handover documentation on every site"
}, {
  id: 3,
  title: "Planned phased erection",
  description: "Scheduled builds and strikes mapped tightly against your sub-contractor trades and crane lifting plans."
}];
const complianceMetrics = [{
  id: 1,
  label: "Accreditations",
  value: "NASC / CHAS"
}, {
  id: 2,
  label: "Frontline Experience",
  value: "20+ Years Direct Experience"
}, {
  id: 3,
  label: "Site Coverage",
  value: "London & South East"
}];
function CapabilityContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const matchedPhoto = posts?.find((p: any) => {
    const photoName = p?.customFields?.photo_name || p?.acf?.photo_name;
    return photoName === "Homepage Commercial Capability Image";
  });
  const customSrc = matchedPhoto?.customFields?.image || (matchedPhoto as any)?.acf?.image;
  const customAlt = matchedPhoto?.customFields?.alt_text || (matchedPhoto as any)?.acf?.alt_text;
  const imgSrc = customSrc || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Newco-One.png";
  const imgAlt = customAlt || "Commercial building with multi-tier scaffolding framework, safety guardrails, and access platforms";
  const isCustom = Boolean(customSrc);
  const motionInitial = shouldReduceMotion ? false : {
    opacity: 0,
    y: 18
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.45,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="commercial-capability" data-nav="light" className="bg-background text-foreground relative border-b border-border py-16 md:py-24 lg:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 md:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12 items-center">
          
          {/* Left Column: Capability Overview */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true,
          amount: 0.2
        }} transition={motionTransition} className="lg:col-span-7 flex flex-col items-start">
            {/* Structural Datum Line */}
            <div className="h-1 w-12 bg-primary rounded-full mb-6" />

            <div className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground mb-4">
              <Building2 className="size-3.5 text-primary" />
              <span>Commercial & Main Contractor Services</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
              Engineered access for major commercial builds.
            </h2>

            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
              From multi-storey facade refurbishments to complex roof replacements, BMF Scaffolding delivers the structural capacity, risk assessments, and dependable workforce required by main contractors, local authorities, and facilities managers across the South East.
            </p>

            {/* Structured Specifications List */}
            <div className="mt-8 grid grid-cols-1 gap-4 w-full">
              {commercialFeatures.map((item, index) => <div key={item.id} data-index={index} className="flex items-start gap-4 p-4 rounded-lg bg-card border border-border transition-colors hover:border-primary/50 shadow-xs">
                  <div className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-card-foreground font-default">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground font-default leading-normal">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Call to Action Row */}
            <div className="mt-10 flex flex-wrap items-center gap-4 w-full">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/CommercialScaffoldingPage" className="inline-flex items-center gap-2">
                  <span>Explore commercial scaffolding</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent font-medium">
                <Link to="/ContactRequestQuotePage">
                  <span>Discuss your project</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Visual Documentation & Specs */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          ...motionTransition,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Primary Site Photography */}
            <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-md group">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={imgSrc} data-wvc-srcset={isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Newco-One.png-wvc-srcset"} data-wvc-sizes={isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Newco-One.png-wvc-sizes"} alt={imgAlt} loading="lazy" decoding="async" onError={e => {
                const target = e.currentTarget;
                if (target.src !== `${window.location.origin}/img/Newco-One.png` && !target.src.endsWith('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Newco-One.png')) {
                  target.src = 'https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Newco-One.png';
                }
              }} className="h-full w-full object-cover transition-[transform] duration-500 group-hover:scale-105" />}
              </div>

              {/* Verified Badge Overlay */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-md bg-secondary/90 px-3 py-1.5 text-xs font-semibold text-secondary-foreground backdrop-blur-xs border border-secondary">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>NASC & TG20:21 Compliant</span>
              </div>
            </div>

            {/* Technical Metric Strip */}
            <div className="rounded-lg border border-border bg-card p-5 shadow-xs divide-y divide-border">
              {complianceMetrics.map((metric, index) => <div key={metric.id} data-index={index} className={`flex items-center justify-between py-3 first:pt-0 last:pb-0`}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground font-default">
                      {metric.label}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-card-foreground font-default">
                    {metric.value}
                  </span>
                </div>)}
            </div>

            {/* Direct Contact / Rapid Response Note */}
            <div className="rounded-lg bg-accent/60 border border-border p-4 flex items-start gap-3.5">
              <div className="rounded-md bg-card p-2 text-primary border border-border shrink-0">
                <FileCheck2 className="size-4" />
              </div>
              <p className="text-xs text-muted-foreground font-default leading-relaxed">
                Comprehensive Risk Assessment and Method Statements (RAMS), plus handover certification on site completion.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}
export default function Capability() {
  const wp_query = new WP_Query({
    post_type: 'site_photo',
    per_page: 100,
    status: ['publish']
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <CapabilityContent />
    </PostsProvider>;
}